import { create } from 'zustand';
import { DateTime } from 'luxon';
import { ISO8601TupleType } from 'types/common.types';
import { hasDataLockRejection } from 'pages/ReportsPage/store/reportsDataHealth.store';
import { DAILY_OVERTIME_DATA_LOCKED_STUB } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/store/dailyOvertimeDashboard.stub';
import {
  DailyOvertimeDashboardStore,
  DailyOvertimeByPosition,
  DailyOvertimeByTeamMemberType,
  DailyOvertimeTrendPoint,
  DailyOvertimeIndividual,
} from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/store/dailyOvertimeDashboard.store.types';
import {
  TotalDailyOvertimeHoursResponse,
  DailyOvertimeRateResponse,
  DailyOvertimeByPositionResponse,
  DailyOvertimeByPositionResponseItem,
  DailyOvertimeByEmploymentStatusResponse,
  DailyOvertimeByEmploymentStatusResponseItem,
  DailyOvertimeDriverRowResponseItem,
  DailyOvertimeDriversResponse,
  DailyOvertimeTrendResponse,
  buildDailyOvertimeQuery,
  mapOvertimeTrendBuckets,
} from 'pages/ReportsPage/components/AnalyticsDashboards/overtimeMappers';
import { formatEmploymentStatusLabel } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard/DailyOvertimeDashboard.constants';
import { toNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/calloutMappers';
import { createDashboardsRequest } from 'pages/ReportsPage/components/AnalyticsDashboards/createDashboardsRequest';
import { unwrapDashboardRoot } from 'pages/ReportsPage/components/AnalyticsDashboards/unwrapDashboardRoot';
import { fetchDashboardPage } from 'pages/ReportsPage/components/AnalyticsDashboards/fetchDashboardPage';
import {
  DASHBOARDS_OVERTIME_TOTAL_DAILY_OVERTIME_HOURS,
  DASHBOARDS_OVERTIME_DAILY_OVERTIME_RATE,
  DASHBOARDS_OVERTIME_DAILY_OVERTIME_BY_POSITION,
  DASHBOARDS_OVERTIME_DAILY_OVERTIME_BY_EMPLOYMENT_STATUS,
  DASHBOARDS_OVERTIME_DAILY_OVERTIME_DRIVERS,
  DASHBOARDS_OVERTIME_DAILY_OVERTIME_TREND,
} from 'constants/endpoints';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';

const INITIAL_STATE = {
  totalOtHours: 0,
  totalOtHoursChangePrct: null as number | null,
  totalOtHoursError: false,
  otRate: 0,
  otRateChangePrct: null as number | null,
  otRateError: false,
  priorPeriodRange: null as ISO8601TupleType | null,
  overtimeByPosition: null as DailyOvertimeByPosition[] | null,
  byPositionPage: 1,
  byPositionTotalPages: 1,
  byPositionLoadingMore: false,
  overtimeByTeamMemberType: null as DailyOvertimeByTeamMemberType[] | null,
  overtimeTrend: null as DailyOvertimeTrendPoint[] | null,
  overtimeIndividuals: null as DailyOvertimeIndividual[] | null,
  individualsPage: 1,
  individualsTotalPages: 1,
  individualsLoading: false,
  isLoading: false,
  error: null as string | null,
};

export function derivePriorPeriodRange(
  dateRange: [string, string],
  periodDays: number | null,
): ISO8601TupleType | null {
  const start = DateTime.fromISO(dateRange[0]).startOf('day');
  if (!start.isValid) return null;
  const rangeDays =
    periodDays && periodDays > 0
      ? Math.round(periodDays)
      : Math.round(DateTime.fromISO(dateRange[1]).startOf('day').diff(start, 'days').days) + 1;
  if (!rangeDays || rangeDays < 1) return null;
  return [
    start.minus({ days: rangeDays }).toString(),
    start.minus({ days: 1 }).endOf('day').toString(),
  ];
}

export function mapOvertimeByPosition(
  rows: DailyOvertimeByPositionResponseItem[],
): DailyOvertimeByPosition[] {
  return rows
    .map((item) => ({
      position: item.position_name,
      overtimeHours: toNumber(item.total_overtime_hours),
    }))
    .sort((a, b) => b.overtimeHours - a.overtimeHours);
}

export function mapOvertimeByEmploymentStatus(
  rows: DailyOvertimeByEmploymentStatusResponseItem[],
): DailyOvertimeByTeamMemberType[] {
  const totalHours = rows.reduce((sum, item) => sum + toNumber(item.total_overtime_hours), 0);
  return rows
    .map((item) => {
      const overtimeHours = toNumber(item.total_overtime_hours);
      return {
        type: formatEmploymentStatusLabel(item.employment_status),
        overtimeHours,
        percentage: totalHours > 0 ? Math.round((overtimeHours / totalHours) * 10000) / 100 : 0,
      };
    })
    .sort((a, b) => b.overtimeHours - a.overtimeHours);
}

export function mapOvertimeDrivers(
  rows: DailyOvertimeDriverRowResponseItem[],
): DailyOvertimeIndividual[] {
  return rows.map((row) => ({
    teamMemberId: row.jane_employee_number || '—',
    teamMemberName: row.jane_name,
    teamMemberType: row.jane_employment_status_label
      ? formatEmploymentStatusLabel(row.jane_employment_status_label)
      : '—',
    overtimeHours: toNumber(row.total_overtime_hours),
  }));
}

let fetchId = 0;
let driversFetchId = 0;
let driversBaseQuery: Record<string, unknown> | null = null;
let byPositionBaseQuery: Record<string, unknown> | null = null;

export const dailyOvertimeDashboardStore = create<DailyOvertimeDashboardStore>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,

    fetchData: (dateRange, locationIds, positions, employmentTypes) => {
      if (isSelectionCleared(locationIds, positions)) {
        fetchId++;
        set({ ...INITIAL_STATE });
        return () => {
          fetchId++;
        };
      }
      const currentFetchId = ++fetchId;
      set({ isLoading: true, error: null, totalOtHoursError: false, otRateError: false });

      const dailyOvertimeQuery = buildDailyOvertimeQuery(
        dateRange,
        locationIds,
        positions,
        employmentTypes,
      );
      driversBaseQuery = dailyOvertimeQuery;
      byPositionBaseQuery = dailyOvertimeQuery;
      driversFetchId++;

      const totalOtHoursRequest = createDashboardsRequest<TotalDailyOvertimeHoursResponse>({
        url: DASHBOARDS_OVERTIME_TOTAL_DAILY_OVERTIME_HOURS(),
        method: 'GET',
        query: dailyOvertimeQuery,
      });

      const otRateRequest = createDashboardsRequest<DailyOvertimeRateResponse>({
        url: DASHBOARDS_OVERTIME_DAILY_OVERTIME_RATE(),
        method: 'GET',
        query: dailyOvertimeQuery,
      });

      const byPositionRequest = fetchDashboardPage<
        DailyOvertimeByPositionResponse,
        DailyOvertimeByPositionResponseItem
      >({
        url: DASHBOARDS_OVERTIME_DAILY_OVERTIME_BY_POSITION(),
        query: dailyOvertimeQuery,
        page: 1,
        selectItems: (body) => body.daily_overtime_by_position ?? [],
      });

      const byEmploymentStatusRequest =
        createDashboardsRequest<DailyOvertimeByEmploymentStatusResponse>({
          url: DASHBOARDS_OVERTIME_DAILY_OVERTIME_BY_EMPLOYMENT_STATUS(),
          method: 'GET',
          query: dailyOvertimeQuery,
        });

      const trendRequest = createDashboardsRequest<DailyOvertimeTrendResponse>({
        url: DASHBOARDS_OVERTIME_DAILY_OVERTIME_TREND(),
        method: 'GET',
        query: dailyOvertimeQuery,
      });

      const driversRequest = createDashboardsRequest<DailyOvertimeDriversResponse>({
        url: DASHBOARDS_OVERTIME_DAILY_OVERTIME_DRIVERS(),
        method: 'GET',
        query: { ...dailyOvertimeQuery, page: 1 },
      });

      Promise.allSettled([
        totalOtHoursRequest,
        otRateRequest,
        byPositionRequest,
        byEmploymentStatusRequest,
        trendRequest,
        driversRequest,
      ]).then((results) => {
        const isDataLocked = hasDataLockRejection(results);
        if (currentFetchId !== fetchId) return;
        if (isDataLocked) {
          set({ ...INITIAL_STATE, ...DAILY_OVERTIME_DATA_LOCKED_STUB, isLoading: false });
          return;
        }
        const [
          totalResult,
          rateResult,
          byPositionResult,
          byStatusResult,
          trendResult,
          driversResult,
        ] = results;

        const totalData = unwrapDashboardRoot<TotalDailyOvertimeHoursResponse>(
          totalResult.status === 'fulfilled' ? totalResult.value?.data ?? null : null,
          'total_daily_overtime_hours',
        );
        const rateData = unwrapDashboardRoot<DailyOvertimeRateResponse>(
          rateResult.status === 'fulfilled' ? rateResult.value?.data ?? null : null,
          'daily_overtime_rate',
        );
        const byPositionData =
          byPositionResult.status === 'fulfilled' ? byPositionResult.value : null;
        const byStatusData =
          byStatusResult.status === 'fulfilled' ? byStatusResult.value.data : null;
        const trendData = trendResult.status === 'fulfilled' ? trendResult.value.data : null;
        const driversData = driversResult.status === 'fulfilled' ? driversResult.value.data : null;

        set((state) => ({
          ...state,
          totalOtHours: toNumber(totalData?.current_period_overtime),
          totalOtHoursChangePrct:
            totalData?.change_prct == null ? null : toNumber(totalData.change_prct),
          totalOtHoursError: totalResult.status === 'rejected',
          priorPeriodRange: derivePriorPeriodRange(
            dateRange,
            totalData?.period_days ?? rateData?.period_days ?? null,
          ),
          otRate: toNumber(rateData?.current_period_overtime_rate),
          otRateChangePrct: rateData?.change_prct == null ? null : toNumber(rateData.change_prct),
          otRateError: rateResult.status === 'rejected',
          overtimeByPosition: byPositionData ? mapOvertimeByPosition(byPositionData.items) : null,
          byPositionPage: byPositionData?.meta?.current_page ?? 1,
          byPositionTotalPages: byPositionData?.meta?.total_pages ?? 1,
          byPositionLoadingMore: false,
          overtimeByTeamMemberType: byStatusData
            ? mapOvertimeByEmploymentStatus(byStatusData.daily_overtime_by_employment_status ?? [])
            : null,
          overtimeTrend: trendData
            ? mapOvertimeTrendBuckets(trendData.daily_overtime_trend?.buckets ?? [])
            : null,
          overtimeIndividuals: driversData
            ? mapOvertimeDrivers(driversData.daily_overtime_drivers ?? [])
            : null,
          individualsPage: driversData?.meta?.current_page ?? 1,
          individualsTotalPages: driversData?.meta?.total_pages ?? 1,
          individualsLoading: false,
          isLoading: false,
          error: null,
        }));
      });

      return () => {
        fetchId++;
      };
    },
    fetchIndividualsPage: (page) => {
      if (!driversBaseQuery) return;
      const currentFetchId = fetchId;
      const currentDriversFetchId = ++driversFetchId;
      set({ individualsLoading: true });

      createDashboardsRequest<DailyOvertimeDriversResponse>({
        url: DASHBOARDS_OVERTIME_DAILY_OVERTIME_DRIVERS(),
        method: 'GET',
        query: { ...driversBaseQuery, page },
      })
        .then(({ data }) => {
          if (currentFetchId !== fetchId || currentDriversFetchId !== driversFetchId) return;
          set({
            overtimeIndividuals: mapOvertimeDrivers(data.daily_overtime_drivers ?? []),
            individualsPage: data.meta?.current_page ?? page,
            individualsTotalPages: data.meta?.total_pages ?? 1,
            individualsLoading: false,
          });
        })
        .catch(() => {
          if (currentFetchId !== fetchId || currentDriversFetchId !== driversFetchId) return;
          set({ individualsLoading: false });
        });
    },
    loadMoreByPosition: () => {
      const { byPositionLoadingMore, byPositionPage, byPositionTotalPages } = get();
      if (byPositionLoadingMore || byPositionPage >= byPositionTotalPages || !byPositionBaseQuery) {
        return;
      }
      const currentFetchId = fetchId;
      const nextPage = byPositionPage + 1;
      set({ byPositionLoadingMore: true });

      fetchDashboardPage<DailyOvertimeByPositionResponse, DailyOvertimeByPositionResponseItem>({
        url: DASHBOARDS_OVERTIME_DAILY_OVERTIME_BY_POSITION(),
        query: byPositionBaseQuery,
        page: nextPage,
        selectItems: (body) => body.daily_overtime_by_position ?? [],
      })
        .then(({ items, meta }) => {
          if (currentFetchId !== fetchId) return;
          set((state) => ({
            overtimeByPosition: [
              ...(state.overtimeByPosition ?? []),
              ...mapOvertimeByPosition(items),
            ],
            byPositionPage: meta?.current_page ?? nextPage,
            byPositionTotalPages: meta?.total_pages ?? state.byPositionTotalPages,
            byPositionLoadingMore: false,
          }));
        })
        .catch(() => {
          if (currentFetchId !== fetchId) return;
          set({ byPositionLoadingMore: false });
        });
    },
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    reset: () => {
      fetchId = 0;
      driversFetchId++;
      driversBaseQuery = null;
      byPositionBaseQuery = null;
      set(INITIAL_STATE);
    },
  })),
);

export const useDailyOvertimeDashboardStore = dailyOvertimeDashboardStore;
